import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  FileText,
  Layers,
  Table2,
} from 'lucide-react'
import { documentTypes, type DocumentType } from '@/data/documentTypes'
import { solutions } from '@/data/solutions'
import { caseStudies } from '@/data/caseStudies'
import { useSEO } from '@/hooks/useSEO'
import { MarketingHeader } from '@/components/marketing/MarketingHeader'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'

/* ──────────────────────────────────────────────────────────
   ExtractList — grid of all document types at /extract
   ────────────────────────────────────────────────────────── */

export function ExtractList() {
  useSEO({
    title: 'Extract Data by Document Type',
    description:
      'Browse all document types supported by DocumentIQ. Extract structured data from invoices, contracts, bills of lading, customs declarations, and more using AI.',
    canonical: 'https://documentiq.algoscale.com/extract',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'DocumentIQ Document Types',
        description: 'All document types supported for AI-powered extraction.',
        url: 'https://documentiq.algoscale.com/extract',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: documentTypes.map((d, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `https://documentiq.algoscale.com/extract/${d.slug}`,
            name: d.name,
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Extract by Document Type', item: 'https://documentiq.algoscale.com/extract' },
        ],
      },
    ],
  })

  const sorted = useMemo(
    () => [...documentTypes].sort((a, b) => a.name.localeCompare(b.name)),
    [],
  )

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-6">
            <Layers size={14} />
            Document Types
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Extract Data by Document Type
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            DocumentIQ supports structured data extraction from dozens of document types.
            Choose a document type to see what fields you can extract.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorted.map((dt) => (
              <Link
                key={dt.slug}
                to={`/extract/${dt.slug}`}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    <FileText size={18} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">
                    {dt.name}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
                  {dt.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-500">{dt.fields.length} extraction fields</span>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                    View fields
                    <ChevronRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gray-50 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
            Don't see your document type?
          </h2>
          <p className="text-gray-600 mb-8">
            DocumentIQ is fully configurable. Define your own fields and extraction prompts for any document format.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-md shadow-blue-600/20 text-sm"
            >
              Start for Free
              <ArrowRight size={18} />
            </Link>
            <a
              href="mailto:askus@algoscale.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
   ExtractDetail — detail page at /extract/:slug
   ────────────────────────────────────────────────────────── */

export function ExtractDetail() {
  const { slug } = useParams<{ slug: string }>()
  const dt = documentTypes.find((d) => d.slug === slug)

  const relatedSolution = dt
    ? solutions.find((s) => s.slug === dt.relatedSolutionSlug)
    : undefined

  const relatedStudies = dt
    ? caseStudies.filter((cs) => dt.relatedCaseStudySlugs.includes(cs.slug))
    : []

  const jsonLd = useMemo(() => {
    if (!dt) return undefined
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: `How to Extract Data from ${dt.plural}`,
        description: dt.description,
        totalTime: 'PT30M',
        step: [
          { '@type': 'HowToStep', position: 1, name: 'Upload', text: `Upload your ${dt.plural.toLowerCase()} (PDF, DOC, DOCX) to DocumentIQ.` },
          { '@type': 'HowToStep', position: 2, name: 'Define Fields', text: `Configure the fields you want to extract, such as ${dt.fields.slice(0, 3).map((f) => f.name).join(', ')}, and more.` },
          { '@type': 'HowToStep', position: 3, name: 'Extract', text: `Run AI extraction. DocumentIQ processes all documents in parallel with confidence scores.` },
          { '@type': 'HowToStep', position: 4, name: 'Review and Export', text: `Review results in a table view, provide feedback on any corrections, then export to CSV, Excel, or PDF.` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Extract', item: 'https://documentiq.algoscale.com/extract' },
          { '@type': 'ListItem', position: 3, name: dt.name, item: `https://documentiq.algoscale.com/extract/${dt.slug}` },
        ],
      },
    ]
  }, [dt])

  useSEO({
    title: dt ? dt.name : 'Extract',
    description: dt?.description || 'Extract structured data from documents with AI.',
    canonical: dt ? `https://documentiq.algoscale.com/extract/${dt.slug}` : undefined,
    jsonLd,
  })

  if (!dt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Document type not found.</p>
          <Link to="/extract" className="text-blue-600 font-medium hover:underline">View all document types</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/extract" className="hover:text-gray-900 transition-colors flex items-center gap-1">
              <ArrowLeft size={14} /> Extract
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{dt.name}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
            {dt.plural}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight mb-6">
            Extract Data from {dt.plural} with AI
          </h1>
          <p className="text-gray-600 leading-relaxed max-w-3xl">{dt.heroDescription}</p>
        </div>
      </section>

      {/* What You Can Extract */}
      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Table2 size={18} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">What You Can Extract</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dt.fields.map((field) => (
              <div
                key={field.name}
                className="p-5 rounded-xl border border-gray-200 bg-white"
              >
                <h3 className="font-semibold text-gray-900 mb-1.5 font-mono text-sm">
                  {field.name}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{field.description}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-500">
            These are common fields. With DocumentIQ you can define any custom field and extraction prompt specific to your documents.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
              <Layers size={18} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">Use Cases</h2>
          </div>
          <ul className="space-y-3">
            {dt.useCases.map((uc, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-700">
                <ChevronRight size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <span>{uc}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related Solution */}
      {relatedSolution && (
        <section className="py-12 sm:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-sm font-semibold text-gray-500 mb-4">Related Industry Solution</h3>
            <Link
              to={`/solutions/industry/${relatedSolution.slug}`}
              className="group flex items-center justify-between p-6 rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all"
            >
              <div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                  {relatedSolution.industry}
                </span>
                <h4 className="text-lg font-semibold text-gray-900 mt-2 group-hover:text-blue-600 transition-colors">
                  {relatedSolution.headline}
                </h4>
                <p className="text-sm text-gray-600 mt-1">{relatedSolution.description}</p>
              </div>
              <ArrowRight size={20} className="text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 ml-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Related Case Studies */}
      {relatedStudies.length > 0 && (
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-sm font-semibold text-gray-500 mb-4">Related Case Studies</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedStudies.map((cs) => (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  className="group p-6 rounded-xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-md transition-all"
                >
                  <div className="text-2xl font-bold text-blue-600 mb-1">{cs.keyMetric.value}</div>
                  <div className="text-xs text-gray-500 mb-3">{cs.keyMetric.label}</div>
                  <h4 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">
                    {cs.headline}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to extract data from your {dt.plural.toLowerCase()}?
          </h2>
          <p className="text-blue-100 mb-8">
            Start extracting structured data in minutes -- no setup fee, no commitment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition-colors shadow-md text-sm"
            >
              Start for Free
              <ArrowRight size={18} />
            </Link>
            <a
              href="mailto:askus@algoscale.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-300 text-white font-medium hover:bg-blue-500/20 transition-colors text-sm"
            >
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  )
}
